import { useEffect, useState } from 'react'
import {
  type EmbedErrorCode,
  type HostInitMessage,
  isAllowedHostOrigin,
  larryReadyMessage,
  parseHostInitMessage,
} from '@/lib/embed/host-messages'
import { postToParent } from '../handlers/post-to-parent'

export type HostHandshakeState =
  | { status: 'waiting' }
  | { status: 'connected'; parentOrigin: string; init: HostInitMessage['payload'] }
  | { status: 'failed'; code: EmbedErrorCode; message: string }

/**
 * Larry's half of the embed handshake: posts `ready` to the parent window, then waits for the
 * host's `init` carrying the session token. Only an `init` from the parent window itself, at one
 * of `allowedOrigins`, is accepted; everything else is ignored. Fails when Larry isn't in an
 * iframe, no host origin is configured, or no `init` arrives within `timeoutMs`.
 *
 * `allowedOrigins` must be stable (config), or the handshake restarts.
 */
export function useHostHandshake(
  allowedOrigins: readonly string[],
  timeoutMs: number,
  enabled = true
): HostHandshakeState {
  const [state, setState] = useState<HostHandshakeState>({ status: 'waiting' })

  useEffect(() => {
    if (!enabled) return

    if (window.parent === window) {
      setState({
        status: 'failed',
        code: 'not-embedded',
        message: 'The embedded editor can only be opened from the spread ladder.',
      })
      return
    }

    if (allowedOrigins.length === 0) {
      setState({
        status: 'failed',
        code: 'not-embedded',
        message: 'Embedding is not configured for this environment (VITE_EMBED_PARENT_ORIGINS).',
      })
      return
    }

    let settled = false

    const handleMessage = (event: MessageEvent) => {
      if (settled || event.source !== window.parent) return
      if (!isAllowedHostOrigin(event.origin, allowedOrigins)) return

      const init = parseHostInitMessage(event.data)

      if (!init) return

      settled = true
      window.clearTimeout(timer)
      setState({ status: 'connected', parentOrigin: event.origin, init: init.payload })
    }

    window.addEventListener('message', handleMessage)

    const timer = window.setTimeout(() => {
      if (settled) return

      settled = true
      setState({
        status: 'failed',
        code: 'init-timeout',
        message: 'The spread ladder did not send a session to open the editor with.',
      })
    }, timeoutMs)

    postToParent(larryReadyMessage(), allowedOrigins)

    return () => {
      window.removeEventListener('message', handleMessage)
      window.clearTimeout(timer)
    }
  }, [allowedOrigins, enabled, timeoutMs])

  return state
}
